// Admin · Directions
// Browse every property's Directions document and edit every field used on
// the public /directions page. Each field is multilingual where the public
// page is multilingual; estimated_times is an editable repeatable list.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RefreshCw, Plus, Trash2, Loader2, CheckCircle2, AlertCircle,
  Image as ImageIcon, FolderOpen, X, Database,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import MediaPicker from "@/components/admin/MediaPicker";
import BunnyStoragePicker from "@/components/admin/BunnyStoragePicker";
import { HOTELS } from "@/lib/data";

const API = process.env.REACT_APP_BACKEND_URL || "";
// Resolve relative API paths (/api/files/...) against the current backend URL.
const resolveImg = (u) => (!u ? "" : u.startsWith("/api/") ? `${API}${u}` : u);
const LANGS = [
  { id: "en", label: "EN" },
  { id: "fr", label: "FR" },
  { id: "es", label: "ES" },
];

const blankI18n = () => ({ en: "", fr: "", es: "" });

const safeReadBody = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

// ── Multilingual textarea group ────────────────────────────────────────
const I18nTextarea = ({ label, value, onChange, rows = 4, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">{label}</p>
    <div className="space-y-2">
      {LANGS.map((l) => (
        <label key={l.id} className="flex gap-2 items-start">
          <span className="mt-2.5 inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.label}
          </span>
          <textarea
            value={value?.[l.id] || ""}
            onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
            rows={rows}
            className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            data-testid={`${testid}-${l.id}`}
          />
        </label>
      ))}
    </div>
  </div>
);

const I18nInput = ({ label, value, onChange, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">{label}</p>
    <div className="space-y-2">
      {LANGS.map((l) => (
        <label key={l.id} className="flex gap-2 items-center">
          <span className="inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.label}
          </span>
          <input
            type="text"
            value={value?.[l.id] || ""}
            onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
            className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            data-testid={`${testid}-${l.id}`}
          />
        </label>
      ))}
    </div>
  </div>
);

// ── Estimated times editor (repeatable list) ───────────────────────────
const EstimatedTimesEditor = ({ rows, onChange }) => {
  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...rows,
      { from: blankI18n(), time: "", distance: "" },
    ]);

  return (
    <div data-testid="admin-directions-times">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
          Estimated travel times
        </p>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:gap-2 transition-all"
          data-testid="admin-directions-times-add"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
          Add row
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-[#5C5248] italic">No travel times yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r, i) => (
            <div
              key={`time-row-${i}`}
              className="border border-[#2C2621]/15 p-4 bg-white"
              data-testid={`admin-directions-times-row-${i}`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
                  Row {i + 1}
                </p>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#C16542] hover:text-[#A35133]"
                  data-testid={`admin-directions-times-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                  Remove
                </button>
              </div>

              <I18nInput
                label="From (city / airport)"
                value={r.from || blankI18n()}
                onChange={(v) => update(i, { from: v })}
                testid={`admin-directions-times-from-${i}`}
              />

              <div className="grid grid-cols-2 gap-3 mt-3">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Time</span>
                  <input
                    type="text"
                    value={r.time || ""}
                    onChange={(e) => update(i, { time: e.target.value })}
                    placeholder="2h 15"
                    className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
                    data-testid={`admin-directions-times-time-${i}`}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Distance</span>
                  <input
                    type="text"
                    value={r.distance || ""}
                    onChange={(e) => update(i, { distance: e.target.value })}
                    placeholder="125 km"
                    className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
                    data-testid={`admin-directions-times-distance-${i}`}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Editor form for one property ───────────────────────────────────────
const PropertyEditor = ({ slug, hotel, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bunnyOpen, setBunnyOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/directions/${slug}`, {
      credentials: "include",
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const save = async () => {
    if (!data) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/directions/${slug}`, {
      credentials: "include",
        method: "PUT",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          fields: {
            coords: data.coords,
            address: data.address,
            directions: data.directions,
            google_maps_url: data.google_maps_url || "",
            phone: data.phone,
            email: data.email,
            airport_transfer: data.airport_transfer,
            travel_tips: data.travel_tips,
            estimated_times: data.estimated_times || [],
            thumbnail_url: data.thumbnail_url || "",
          },
        }),
      });
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const next = await res.json();
      setData(next);
      setSavedAt(new Date());
      onSaved && onSaved(slug);
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this property to the static defaults? Any custom overrides will be lost.")) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/directions/${slug}`, {
      credentials: "include",
        method: "DELETE",
        headers: adminHeaders(),
      });
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      await load();
      setSavedAt(new Date());
      onSaved && onSaved(slug);
    } catch (e) {
      setError(e.message || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-[#5C5248]" data-testid="admin-directions-editor-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) {
    return (
      <p className="p-8 text-sm text-[#C16542]">{error || "No data"}</p>
    );
  }

  const isOverride = !!data.is_override;

  return (
    <div className="space-y-8" data-testid="admin-directions-editor">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[#C16542]">{hotel?.category || "property"}</p>
          <h2 className="font-serif-x text-3xl md:text-4xl tracking-tight mt-1">
            {hotel?.name || slug}
          </h2>
          <p className="mt-1 text-xs text-[#5C5248]">
            Slug: <span className="tabular-nums">{slug}</span>
            {isOverride ? " · Custom CMS override active" : " · Using static defaults"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedAt ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#5C7C4F]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Saved {savedAt.toLocaleTimeString()}
            </span>
          ) : null}
          {isOverride ? (
            <button
              type="button"
              onClick={reset}
              disabled={saving}
              className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
              data-testid="admin-directions-reset"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />
              Reset to defaults
            </button>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-directions-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.8} />}
            Save
          </button>
        </div>
      </header>

      {error ? (
        <div className="border border-[#C16542]/40 bg-[#C16542]/5 px-4 py-3 text-sm text-[#C16542] flex items-center gap-2" data-testid="admin-directions-error">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      ) : null}

      {/* Thumbnail image */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
            Thumbnail image
          </p>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5248]/80">
            {data.thumbnail_url ? "Custom override" : "Using default hotel image"}
          </span>
        </div>
        <div className="grid md:grid-cols-[260px_1fr] gap-5 items-start">
          {/* Preview */}
          <div className="relative aspect-[4/3] bg-[#F3EBDD] border border-[#2C2621]/15 overflow-hidden">
            {(data.thumbnail_url || hotel?.image) ? (
              <img
                src={resolveImg(data.thumbnail_url || hotel?.image)}
                alt={hotel?.name || slug}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="admin-directions-thumb-preview" loading="lazy" decoding="async"/>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#5C5248]">
                <ImageIcon className="w-8 h-8" strokeWidth={1.2} />
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-3">
            <label className="block">
              <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">
                Image URL <span className="text-[#5C5248]/70 normal-case">(absolute URL or `/api/files/...`)</span>
              </span>
              <input
                type="text"
                value={data.thumbnail_url || ""}
                onChange={(e) => update({ thumbnail_url: e.target.value })}
                placeholder={hotel?.image || "https://…"}
                className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] font-mono"
                data-testid="admin-directions-thumb-url"
              />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setBunnyOpen(true)}
                className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                data-testid="admin-directions-thumb-pick-bunny"
                title="Browse the Bunny Storage Zone and pick the thumbnail directly"
              >
                <Database className="w-3.5 h-3.5" strokeWidth={1.6} />
                Select from Bunny Store
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                data-testid="admin-directions-thumb-pick"
              >
                <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.6} />
                Select from Library
              </button>
              {data.thumbnail_url ? (
                <button
                  type="button"
                  onClick={() => update({ thumbnail_url: "" })}
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133] transition-colors"
                  data-testid="admin-directions-thumb-clear"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Clear override
                </button>
              ) : null}
            </div>
            <p className="text-xs text-[#5C5248] leading-relaxed">
              Leave blank to use the default property image from the static brand registry. Custom uploads from the Media Library will override the default both on <code>/directions</code> and in the regional map.
            </p>
          </div>
        </div>
      </section>

      {pickerOpen ? (
        <MediaPicker
          onClose={() => setPickerOpen(false)}
          onSelect={(asset) => {
            update({ thumbnail_url: asset?.url || "" });
            setPickerOpen(false);
          }}
          title={`Thumbnail for ${hotel?.name || slug}`}
          confirmLabel="Use this image"
        />
      ) : null}

      {bunnyOpen ? (
        <BunnyStoragePicker
          onClose={() => setBunnyOpen(false)}
          onSelect={(file) => {
            const f = Array.isArray(file) ? file[0] : file;
            update({ thumbnail_url: f?.public_url || "" });
            setBunnyOpen(false);
          }}
          title={`Thumbnail for ${hotel?.name || slug} — Bunny Storage`}
        />
      ) : null}

      {/* Coordinates */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-3">GPS coordinates</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Latitude</span>
            <input
              type="number"
              step="0.0001"
              value={data.coords?.[0] ?? ""}
              onChange={(e) => update({ coords: [parseFloat(e.target.value) || 0, data.coords?.[1] ?? 0] })}
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
              data-testid="admin-directions-lat"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Longitude</span>
            <input
              type="number"
              step="0.0001"
              value={data.coords?.[1] ?? ""}
              onChange={(e) => update({ coords: [data.coords?.[0] ?? 0, parseFloat(e.target.value) || 0] })}
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
              data-testid="admin-directions-lng"
            />
          </label>
        </div>
      </section>

      {/* Address */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <I18nTextarea
          label="Full address"
          rows={2}
          value={data.address}
          onChange={(v) => update({ address: v })}
          testid="admin-directions-address"
        />
      </section>

      {/* Directions */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <I18nTextarea
          label="Step-by-step directions"
          rows={4}
          value={data.directions}
          onChange={(v) => update({ directions: v })}
          testid="admin-directions-directions"
        />
      </section>

      {/* Google Maps URL */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">
            Google Maps URL <span className="text-[#5C5248]/70 normal-case">(leave blank to hide the navigation button)</span>
          </span>
          <input
            type="url"
            value={data.google_maps_url || ""}
            onChange={(e) => update({ google_maps_url: e.target.value })}
            placeholder="https://maps.app.goo.gl/..."
            className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] font-mono"
            data-testid="admin-directions-maps-url"
          />
        </label>
      </section>

      {/* Contact */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Contact email</span>
          <input
            type="email"
            value={data.email || ""}
            onChange={(e) => update({ email: e.target.value })}
            placeholder="xaluca@xaluca.com"
            className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
            data-testid="admin-directions-email"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Contact telephone</span>
          <input
            type="tel"
            value={data.phone || ""}
            onChange={(e) => update({ phone: e.target.value })}
            placeholder="+212 535 57 84 50"
            className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
            data-testid="admin-directions-phone"
          />
        </label>
      </section>

      {/* Airport transfer */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <I18nTextarea
          label="Airport transfer information"
          rows={3}
          value={data.airport_transfer}
          onChange={(v) => update({ airport_transfer: v })}
          testid="admin-directions-airport"
        />
      </section>

      {/* Travel tips */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <I18nTextarea
          label="Travel tips"
          rows={3}
          value={data.travel_tips}
          onChange={(v) => update({ travel_tips: v })}
          testid="admin-directions-tips"
        />
      </section>

      {/* Estimated travel times */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <EstimatedTimesEditor
          rows={data.estimated_times || []}
          onChange={(rows) => update({ estimated_times: rows })}
        />
      </section>
    </div>
  );
};

// ── Page shell ─────────────────────────────────────────────────────────
const AdminDirectionsInner = () => {
  const [selected, setSelected] = useState(HOTELS[0]?.slug || "");
  const [overrideMap, setOverrideMap] = useState({});

  // Initial load of the full list to know which slugs have overrides.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/directions`, {
      credentials: "include", headers: adminHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data?.items || []).forEach((it) => { map[it.hotel_slug] = !!it.is_override; });
        setOverrideMap(map);
      })
      .catch(() => { /* ignore — list view still works */ });
    return () => { cancelled = true; };
  }, []);

  const handleSaved = (slug) => {
    setOverrideMap((m) => ({ ...m, [slug]: true }));
  };

  const selectedHotel = useMemo(
    () => HOTELS.find((h) => h.slug === selected),
    [selected]
  );

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-directions-page">
      <header className="px-6 md:px-10 py-8 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] mb-3"
              data-testid="admin-directions-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to admin
            </Link>
            <p className="overline text-[#C16542]">Grup Xaluca · Admin · Directions</p>
            <h1 className="font-serif-x text-4xl md:text-5xl tracking-tight mt-1">
              Directions & arrival information
            </h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Manage GPS, addresses, step-by-step directions, estimated travel times, Google Maps links, contact info and travel tips for every property — in English, French and Spanish.
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar — property selector */}
        <aside className="lg:sticky lg:top-28 self-start" data-testid="admin-directions-sidebar">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-3">Properties</p>
          <ul className="space-y-1">
            {HOTELS.map((h) => {
              const active = h.slug === selected;
              const hasOverride = !!overrideMap[h.slug];
              return (
                <li key={h.slug}>
                  <button
                    type="button"
                    onClick={() => setSelected(h.slug)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors ${
                      active
                        ? "bg-[#2C2621] text-[#FDFBF7]"
                        : "hover:bg-[#F2EBE1] text-[#2C2621]"
                    }`}
                    data-testid={`admin-directions-pick-${h.slug}`}
                  >
                    <span className="truncate">{h.name}</span>
                    {hasOverride ? (
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#D4A373]" : "bg-[#C16542]"}`} title="Has custom override" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#5C5248]">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C16542] mr-2" />
            Custom override active
          </p>
        </aside>

        {/* Right pane — editor */}
        <main>
          {selectedHotel ? (
            <PropertyEditor
              key={selected}
              slug={selected}
              hotel={selectedHotel}
              onSaved={handleSaved}
            />
          ) : (
            <p className="text-sm text-[#5C5248]">Pick a property on the left.</p>
          )}
        </main>
      </div>
    </section>
  );
};

const AdminDirectionsPage = () => (
  <AdminAuthGate>
    <AdminDirectionsInner />
  </AdminAuthGate>
);

export default AdminDirectionsPage;
